<script setup lang="ts">
import { computed, inject, ref, watch, type Ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { storeToRefs } from 'pinia';
import { createQueryParams } from '@/utils/query-params';
import { useTableFilter } from '@/composables/useTableFilter';
import { useFileDownload } from '@/composables/useFileDownload';
import { caregiversApi, type XlsxCaregivers } from '@/api/caregivers';
import { communesApi } from '@/api/communes';
import { type SerializedUser } from '@/api/users';
import rutHelpers from 'rut-helpers';
import { getCareerColor } from '@/utils/careers';
import { usePermissions } from '@/composables/usePermissions';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import { useGroupFilterStore } from '@/stores/group-filter';
import GroupFilterSelect from '@/components/group-filter-select.vue';
import ListToolbar from '@/components/list-toolbar.vue';
import CaregiverDiscardForm from './caregiver-discard-form.vue';
import CaregiverUndiscardForm from './caregiver-undiscard-form.vue';

const { hasPermission } = usePermissions();
const { downloadFile } = useFileDownload();
const { tableProps } = useResponsiveTable({ desktopItemsPerPage: 10 });

const headers = [
  { title: 'Nombre', value: 'firstName', sortable: true },
  { title: 'Apellido', value: 'lastName', sortable: true },
  { title: 'RUT', value: 'dni' },
  { title: 'Teléfono', value: 'phoneNumber' },
  { title: 'Comuna', value: 'communeName' },
  { title: 'Especialidad', value: 'humanCareer', sortable: true },
];

const currentUser = inject<Ref<SerializedUser>>('currentUser');
// Caregivers of a company connected to an ERP are created there.
const erpManaged = computed(() => !!currentUser?.value?.companyErpManagedCaregivers);
if (currentUser?.value?.companyUsesIdCode) {
  headers.unshift({ title: 'Código', value: 'idCode', sortable: true });
}

const { groupIds } = storeToRefs(useGroupFilterStore());

const localFilters = ref<{ communeIdEq: undefined | string }>({ communeIdEq: undefined });

const activeFilterCount = computed(() => [
  localFilters.value.communeIdEq,
  groupIds.value.length > 0 || undefined,
].filter(Boolean).length);
const queryParams = computed(() => createQueryParams({
  ...localFilters.value,
  groupsIdIn: groupIds.value,
}));

const { data: communes } = useQuery({
  queryKey: ['communes'],
  queryFn: () => communesApi.index('caregivers'),
});

const { data: activeCaregivers, refetch: refetchActiveCaregivers, isPending, isFetching } = useQuery({
  queryKey: ['activeCaregivers'],
  queryFn: () => caregiversApi.indexForList('active', queryParams.value),
});

const { data: discardedCaregivers, refetch: refetchDiscardedCaregivers } = useQuery({
  queryKey: ['discardedCaregivers'],
  queryFn: () => caregiversApi.indexForList('discarded', queryParams.value),
});

const scope = ref('active');
const caregivers = computed(
  () => (scope.value === 'active' ? activeCaregivers.value : discardedCaregivers.value),
);

const hasActions = computed(() =>
  hasPermission.value('caregivers', 'show') ||
  hasPermission.value('caregivers', 'update') ||
  hasPermission.value('caregivers', 'destroy'),
);

if (hasActions.value) {
  headers.push({ title: 'Acciones', value: 'actions' });
}

const actionSuccess = ref(false);

watch(queryParams, () => {
  refetchActiveCaregivers();
  refetchDiscardedCaregivers();
});

const search = ref('');

const multiWordAccentInsensitiveFilter = useTableFilter([
  'firstName',
  'lastName',
  'phoneNumber',
  'communeName',
  'humanCareer',
  'idCode',
], { rutFields: ['dni'] });

const xlsxDownloadSnackbarAlert = ref(false);
function downloadCaregiversXlsx() {
  xlsxDownloadSnackbarAlert.value = true;

  caregiversApi.indexAsXlsx(scope.value, groupIds.value).then((response: XlsxCaregivers) => {
    downloadFile(response);
  });
}

</script>
<template>
  <v-card class="w-100">
    <v-data-table
      v-bind="tableProps"
      :headers="headers"
      :header-props="{ class: 'font-weight-bold' }"
      :items="caregivers"
      :search="search"
      :custom-filter="multiWordAccentInsensitiveFilter"
      :loading="isPending"
      loading-text="Cargando los profesionales..."
    >
      <template #top>
        <list-toolbar
          v-model:search="search"
          title="Profesionales"
          search-label="Buscar"
          :active-filter-count="activeFilterCount"
        >
          <template #actions>
            <v-btn
              v-if="scope === 'active'"
              color="primary"
              prepend-icon="mdi-microsoft-excel"
              variant="outlined"
              :disabled="isFetching || xlsxDownloadSnackbarAlert"
              @click="downloadCaregiversXlsx"
            >
              Descargar
            </v-btn>
            <v-btn
              v-if="hasPermission('caregivers', 'create') && !erpManaged"
              class="text-white border bg-primary"
              href="caregivers/new"
            >
              Agregar
            </v-btn>
          </template>
          <template #tabs>
            <v-tabs
              v-model="scope"
              color="primary"
            >
              <v-tab
                value="active"
                class="rounded-pill text-body-1"
              >
                Activos
              </v-tab>
              <v-tab
                value="discarded"
                class="rounded-pill text-body-1"
              >
                Archivados
              </v-tab>
            </v-tabs>
          </template>
          <template #filters>
            <v-autocomplete
              v-model="localFilters.communeIdEq"
              :items="communes"
              item-title="name"
              item-value="id"
              label="Comuna"
              single-line
              clearable
              variant="underlined"
              hide-details
            />
            <group-filter-select
              v-model="groupIds"
              hide-details
            />
          </template>
        </list-toolbar>
        <v-snackbar
          v-model="xlsxDownloadSnackbarAlert"
          :timeout="2000"
          color="primary"
          rounded="pill"
        >
          Se está generando el archivo Excel, puede tomar unos segundos...
        </v-snackbar>
      </template>
      <template #[`item.dni`]="{ item }">
        {{ item.dni ? rutHelpers.rutFormat(item.dni) : '' }}
      </template>
      <template #[`item.humanCareer`]="{ item }">
        <v-chip :color="getCareerColor(item.career)">
          {{ item.humanCareer }}
        </v-chip>
      </template>
      <template #[`item.actions`]="{ item }">
        <div
          v-if="scope === 'active'"
          class="d-flex"
        >
          <v-btn
            v-if="hasPermission('caregivers', 'show')"
            icon="mdi-eye"
            size="small"
            class="me-2"
            :href="`/caregivers/${item.id}`"
          />
          <v-btn
            v-if="hasPermission('caregivers', 'update')"
            icon="mdi-pencil"
            size="small"
            class="me-2"
            :href="`/caregivers/${item.id}/edit`"
          />
          <v-dialog
            max-width="800"
            scrollable
          >
            <template #activator="{ props: activatorProps }">
              <v-btn
                v-if="hasPermission('caregivers', 'destroy') && !item.erpManaged"
                v-bind="activatorProps"
                icon="mdi-archive"
                size="small"
              />
            </template>
            <template #default="{ isActive }">
              <caregiver-discard-form
                :caregiver="item"
                @close="isActive.value = false"
                @success="actionSuccess = true"
                @refetch="refetchActiveCaregivers(); refetchDiscardedCaregivers();"
              />
            </template>
          </v-dialog>
        </div>
        <v-dialog
          v-else-if="scope === 'discarded'"
          max-width="800"
          scrollable
        >
          <template #activator="{ props: activatorProps }">
            <v-btn
              v-if="hasPermission('caregivers', 'update') && !item.erpManaged"
              v-bind="activatorProps"
              icon="mdi-package-up"
              size="small"
            />
          </template>
          <template #default="{ isActive }">
            <caregiver-undiscard-form
              :caregiver="item"
              @close="isActive.value = false"
              @success="actionSuccess = true"
              @refetch="refetchActiveCaregivers(); refetchDiscardedCaregivers();"
            />
          </template>
        </v-dialog>
      </template>
    </v-data-table>
    <v-snackbar
      v-model="actionSuccess"
      :timeout="2000"
      color="success"
      rounded="pill"
    >
      <v-icon
        icon="mdi-check-circle"
        class="mr-4"
      />
      Cambios guardados exitosamente!
    </v-snackbar>
  </v-card>
</template>
